/**
 * Whole cents, and the two conversions every screen needs.
 *
 * WHY THIS FILE EXISTS
 *
 * `formatCents` had been written twice — once in EditorPage, once in
 * TriagePage — and the clearings screen would have been the third. CLAUDE.md's
 * rule is that a derived quantity several callers need lives in one place, and
 * the argument there is about drift producing no error: two copies that round
 * differently, or one that grows a thousands separator and one that does not,
 * show an operator the same amount written two ways on two screens and nothing
 * anywhere reports a problem.
 *
 * `parseCents` moved for a sharper reason. It carries a documented float
 * hazard and a mutation that had to be re-run before it could fail (see
 * apps/web/test/README.md), and a second copy would be a second chance to
 * write `Number(text) * 100` — which passes casual testing and is wrong by a
 * fraction of a cent on ordinary amounts.
 */

/**
 * Cents to dollars for display.
 *
 * Amounts are whole cents everywhere in this system (§7) and arrive as
 * numbers; this never does arithmetic on them beyond splitting off the sign,
 * so no rounding can creep in.
 */
export function formatCents(cents: number): string {
  const negative = cents < 0;
  const absolute = Math.abs(cents);
  const dollars = Math.floor(absolute / 100).toLocaleString('en-US');
  return `${negative ? '-' : ''}$${dollars}.${String(absolute % 100).padStart(2, '0')}`;
}

/**
 * Dollars to whole cents.
 *
 * Deliberately not `Number(text) * 100`. Binary floating point cannot hold
 * most two-decimal amounts, and the products are off by a fraction:
 *
 *     19.99 * 100 === 1998.9999999999998
 *      4.35 * 100 ===  434.99999999999994
 *      8.22 * 100 ===  822.0000000000001
 *      1.13 * 100 ===  112.99999999999999
 *
 * Those four values are in the test, and they are there because the first
 * version of this comment cited `47.32 * 100` — which is exactly 4732. The
 * hazard was real, the example was invented, and the mutation replacing this
 * function with the naive multiplication passed the entire suite until the
 * measured values went in.
 *
 * Returns null for anything unparseable, and 0 for an empty field. The two are
 * different: a blank line is a line with no amount, and "abc" is a mistake the
 * operator has to see.
 */
export function parseCents(text: string): number | null {
  // $ and thousands separators are stripped because people paste amounts, and
  // "$1,048.50" is not ambiguous. Nothing else is guessed at.
  const cleaned = text.trim().replace(/[$,\s]/g, '');
  if (cleaned === '') return 0;

  /*
   * The leading digits are OPTIONAL. ".45" is how a great many people type
   * forty-five cents, and the first version required a digit before the point,
   * so it was refused as "an amount is not a number" -- on a $0.45 cashback
   * transaction, where typing ".45" is the obvious thing to do.
   *
   * A trailing point is allowed ("45.") because it happens mid-typing and the
   * field is read on every keystroke. A bare "." is not: there is no number in
   * it.
   */
  if (!/^(?:\d{1,12}(?:\.\d{0,2})?|\.\d{1,2})$/.test(cleaned)) return null;

  const [whole, fraction = ''] = cleaned.split('.');
  return Number(whole || '0') * 100 + Number(fraction.padEnd(2, '0'));
}

/**
 * Cents as the text an amount INPUT holds. `104850` -> `"1,048.50"`.
 *
 * WHY THIS IS NOT `formatCents`
 *
 * `formatCents` prefixes a dollar sign and renders a negative with a leading
 * minus. An input holds neither: the sign is carried by which column the
 * amount is in (§7 — a line is a debit or a credit, never a negative of the
 * other), and a `$` typed back into the field is noise the parser then has to
 * strip. So this is the same thousands grouping with none of the decoration.
 *
 * WHY IT HAS A THOUSANDS SEPARATOR AT ALL
 *
 * The editor already normalised on blur, to two decimal places, with
 * `(cents / 100).toFixed(2)` — so `104850` became `1048.50`. Every other
 * amount on every screen goes through `formatCents` and reads `$1,048.50`, so
 * the one place the operator TYPES money was the one place it was rendered
 * without grouping. Four digits and five digits look alike at a glance in a
 * monospace column, which is the error this exists to prevent.
 *
 * ROUND-TRIPPING IS SAFE, AND THAT IS NOT A COINCIDENCE. `parseCents` already
 * strips `$`, commas and whitespace before parsing, because people paste
 * amounts — so the value this produces can be read straight back in. Adding
 * grouping here without that would have made every second blur reject the
 * field's own contents.
 */
export function formatAmountInput(cents: number): string {
  const dollars = Math.floor(Math.abs(cents) / 100).toLocaleString('en-US');
  return `${cents < 0 ? '-' : ''}${dollars}.${String(Math.abs(cents) % 100).padStart(2, '0')}`;
}

/**
 * What an amount field should hold once the operator has finished typing.
 *
 * Left EXACTLY as typed when it does not parse, so the mistake stays visible
 * and points at what was actually entered. An empty field stays empty rather
 * than becoming `0.00`: a blank line is a line with no amount, and filling it
 * in would be the screen making an entry the operator did not.
 */
export function normaliseAmountInput(text: string): string {
  if (text.trim() === '') return '';
  const cents = parseCents(text);
  return cents === null ? text : formatAmountInput(cents);
}
